import { useEffect, useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import apiFetch from '@/services/apiService'
import { formatPrice } from '@/utils/formatters'
import { Input, SectionPanel, Select, Spinner, Toggle } from '@/components/ui'
import { createDocument, documentTypeOptions, emptyDocument, setDocumentInEdition } from '../documentsSlice'
import ContactFields from '../../contacts/form/ContactFields'
import SuggestionBienField from '../../biens/SuggestionField'
import { addFlashMessage } from '../../flash/flashMessagesSlice'

export default ({}) => {
  const dispatch = useDispatch()
  const document = useSelector( state => state.documents.inEdition || emptyDocument)
  const requestSuccessful = useSelector((state) => state.documents.requestStatus.success)
  const errors = useSelector((state) => state.documents.requestStatus.errors)
  const isCreating = useSelector((state) => state.documents.loadingStatus.createBien)
  
  // Référence pour le composant ContactFields
  const contactFieldsRef = useRef(null)
  
  // États pour la gestion des locations
  const [locations, setLocations] = useState([])
  const [selectedLocationId, setSelectedLocationId] = useState(null)
  const [isLoadingLocations, setIsLoadingLocations] = useState(false)

  const { handleSubmit, register, reset, getValues, setValue, control, watch, setError, formState: { errors: formErrors } } = useForm({
    defaultValues: document,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const file = watch('file')
  const typeDocument = watch('type_document')
  const bien = watch('bien')

  const isAncienneQuittance = () => typeDocument === "ancienne_quittance"
  const isDecompte = () => typeDocument === "decompte"
  
  // Générer les options pour les mois
  const monthOptions = [
    { value: 1, label: 'Janvier' },
    { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' },
    { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Décembre' }
  ]

  // Générer les options pour les années (5 ans en arrière jusqu'à l'année courante)
  const currentYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: 6 }, (_, i) => ({
    value: currentYear - 5 + i,
    label: `${currentYear - 5 + i}`
  }))
  
  // Fonction pour charger les locations d'un bien
  const loadLocationsForBien = async (bienId) => {
    if (!bienId) return
    
    setIsLoadingLocations(true)
    try {
      // const response = await wretch(`/api/v1/biens/${bienId}/locations`)
      const response = await apiFetch()
        .get(`biens/${bienId}/locations`)
      
      // Filtrer les locations pour n'inclure que celles avec une date d'effet de bail
      const filteredLocations = response.locations.filter(location => location.date_effet_bail)
      
      setLocations(filteredLocations || [])
    } catch (error) {
      console.error('Erreur lors du chargement des locations:', error)
    } finally {
      setIsLoadingLocations(false)
    }
  }

  useEffect(() => {
    if (document) {
      Object.entries(document).forEach(([key, value]) => {
        setValue(key, value)
      })
    }
  }, [document, setValue])

  useEffect(() => {
    const titre = getValues('titre')
    if(file && file instanceof File && (!titre || titre.length == 0)) {
      // Extraire le nom du fichier sans l'extension
      const lastDotIndex = file.name.lastIndexOf('.')
      const fileNameWithoutExtension = lastDotIndex !== -1 ? file.name.substring(0, lastDotIndex) : file.name
      setValue('titre', fileNameWithoutExtension)
    }
  }, [file])

  const onReset = () => {
    reset()
    // Réinitialiser le document en édition dans le store
    dispatch(setDocumentInEdition(null))

    // Appeler directement la méthode resetFields sur le composant ContactFields
    if (contactFieldsRef.current) {
      contactFieldsRef.current.resetFields()
    }
  }

  useEffect(() => {
    if (!isCreating && requestSuccessful && (!errors || Object.keys(errors).length === 0)) {
      onReset()
    } 
  }, [isCreating, requestSuccessful, errors])

  // Observer les changements de bien pour charger les locations
  useEffect(() => {
    if (bien && bien.id && isAncienneQuittance()) {
      loadLocationsForBien(bien.id)
    } else {
      setLocations([])
      setSelectedLocationId(null)
    }
  }, [bien, typeDocument])
 
  // Initialiser les valeurs par défaut lorsque le type de document change
  useEffect(() => {
    if (isAncienneQuittance()) {
      // Définir des valeurs par défaut pour le mois et l'année
      const currentDate = new Date()
      setValue('quittance_month', currentDate.getMonth() + 1)
      setValue('quittance_year', currentDate.getFullYear())
      
      setValue('titre', null)
      // Activer l'affichage pour les locataires par défaut
      setValue('display_to_locataire', true)
    } else {
      // Réinitialiser les champs spécifiques aux quittances
      setValue('quittance_month', null)
      setValue('quittance_year', null)
      setValue('location_id', null)

      if ( isDecompte() ) {
        toggleOffDisplays()
        setValue("display_to_proprietaire", true)
        setValue('bien_id', null)
        window.document.querySelector("button.new-owner")?.click()
      }
    } 
  }, [typeDocument, setValue])

  const onSubmit = async (formData) => {
    // Validation spécifique pour les quittances anciennes
    if (formData.type_document === 'ancienne_quittance') {
      let hasError = false
      
      if (!formData.location_id) {
        dispatch(addFlashMessage({type: "error", text: 'Veuillez sélectionner une location.'}))
        hasError = true
      }
      
      if (!formData.quittance_month) {
        setError('quittance_month', {
          type: 'manual',
          message: 'Veuillez sélectionner un mois.'
        })
        hasError = true
      }
      
      if (!formData.quittance_year) {
        setError('quittance_year', {
          type: 'manual',
          message: 'Veuillez sélectionner une année.'
        })
        hasError = true
      }
      
      if (hasError) {
        return
      }
    }
    
    // Validation spécifique pour les décomptes
    if (formData.type_document === 'decompte') {
      let hasError = false
      
      // Vérifier si au moins un contact est sélectionné
      if (!formData.contacts || formData.contacts.length === 0) {
        dispatch(addFlashMessage({type: "error", text: 'Veuillez sélectionner au moins un contact propriétaire.'}))
        hasError = true
      }
      
      if (hasError) {
        return
      }
    }
    
    dispatch(createDocument(formData))
  }

  const handleReset = (e) => {
    e.preventDefault()
    e.stopPropagation()
    onReset() 
  }

  const toggleOffDisplays = () => {
    setValue("display_to_proprietaire", false)
    setValue("display_to_locataire", false)
    setValue("display_to_acquereur", false)
  }

  const getErrorMessage = (fieldName) =>
    formErrors?.[fieldName]?.message || errors?.[fieldName] || ''

  const hasError = (fieldName) => {
    if(fieldName === "titre" && isAncienneQuittance()) {
      return null
    }
    return !!formErrors?.[fieldName] || !!errors?.[fieldName] 
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
    <SectionPanel>
      <SectionPanel.Body columns={3}>
        <div className='flex flex-col gap-4'>
          <Input.Text name='titre'
            label="Titre du document"
            className="w-full"
            control={control}
            rules={ !isAncienneQuittance() ? 
              { required: "Le titre du document doit être renseigné." } : 
              {}
            }
            disabled={isAncienneQuittance()}
            validator={{hasError, getErrorMessage}}
          />
          <Select.Form name='type_document'
            label="Type de document"
            control={control}
            options={documentTypeOptions}
            rules={{
              required: "Le type de document doit être défini."
            }}
            validator={{hasError, getErrorMessage}}
          />
          {!isAncienneQuittance() ? (
            <Input.Date name="date_disponibilite"
              label="Date de disponibilité"
              control={control}
              rules={{
                required: "doit être renseignée."
              }}
              validator={{hasError, getErrorMessage}}
            />
          ) : (
            <div className="mb-4">
              <h3 className="font-bold mb-2">Période de la quittance</h3>
              <div className="flex gap-4">
                <div className="w-1/2">
                  <Select.Form name='quittance_month'
                    label="Mois"
                    control={control}
                    options={monthOptions}
                    rules={{
                      required: "Le mois doit être défini."
                    }}
                    validator={{hasError, getErrorMessage}}
                  />
                </div>
                <div className="w-1/2">
                  <Select.Form name='quittance_year'
                    label="Année"
                    control={control}
                    options={yearOptions}
                    rules={{
                      required: "L'année doit être définie."
                    }}
                    validator={{hasError, getErrorMessage}}
                  />
                </div>
              </div>
            </div>
          )}         
        </div>

        <div className='flex flex-col gap-4'>
          <Controller
            name="bien"
            control={control}
            defaultValues={""}
            rules={{
              required: !isDecompte() ? "Le bien doit être défini." : false
            }}
            render={({field}) => (
              <SuggestionBienField
                objectField={field}
                originalData={document.bien}
                validator={{hasError, getErrorMessage}}
                disabled={isDecompte()}
              />
            )}
          />

          <div className='flex flex-col pl-2 space-y-4'>
            <h3 className='font-bold'>Affichage :</h3>
            <Toggle.Form 
              control={control} 
              name="display_to_proprietaire"
              disabled={isAncienneQuittance() || isDecompte()}
              label="Propriétaire" 
            />
            <Toggle.Form 
              control={control} 
              name="display_to_locataire" 
              disabled={isAncienneQuittance() || isDecompte()}
              label="Locataire" 
            />
            <Toggle.Form 
              control={control} 
              name="display_to_acquereur" 
              disabled={isAncienneQuittance() || isDecompte()}
              label="Acquéreur" 
            />

            { isDecompte() &&
              <ContactFields
                ref={contactFieldsRef}
                control={control}
                validator={{hasError, getErrorMessage}}
                selectedCallBack={toggleOffDisplays}
                nature="proprietaires"
              />
            }

            { !isAncienneQuittance() && !isDecompte() &&
              <ContactFields
                ref={contactFieldsRef}
                control={control}
                validator={{hasError, getErrorMessage}}
                selectedCallBack={toggleOffDisplays}
              />
            }
          </div>

        </div>
        <div className='flex flex-col gap-4'>
          <Input.File name='file'
            label="Document à charger"
            defaultValue={""}
            control={control}
            inputProps={{accept: [".pdf", "image/*"]}}
            rules={{
              required: "un fichier doit être chargé."
            }}
            validator={{hasError, getErrorMessage}}
            className='w-full'
          />
        </div>
      
        {isAncienneQuittance() && bien && bien.id && (
          <div className="mb-4 col-span-3">
            <h3 className="font-bold mb-2">Locations</h3>
            {isLoadingLocations ? (
              <div className="flex justify-center items-center p-4">
                <Spinner />
                <span className="ml-2">Chargement des locations...</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table table-compact w-full">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Date d'effet</th>
                      <th>Date de fin</th>
                      <th>Loyer</th>
                      <th>Locataires</th>
                    </tr>
                  </thead>
                  <tbody>
                    { locations.length === 0 && <tr><td colSpan="5" className="text-center">
                        Aucune location n'a été trouvée pour ce bien.
                      </td></tr>
                    }
                    {locations.map(location => (
                      <tr key={location.id} className={selectedLocationId === location.id ? 'bg-primary-400 bg-opacity-20' : ''}
                          onClick={(e) => {
                            setSelectedLocationId(location.id)
                            setValue('location_id', location.id)
                          }}
                      >
                        <td>
                          <input
                            type="radio"
                            name="location_id_radio"
                            className="radio radio-sm radio-primary"
                            checked={selectedLocationId === location.id}
                            onChange={() => {
                              setSelectedLocationId(location.id)
                              setValue('location_id', location.id)
                            }}
                          />
                        </td>
                        <td>{new Date(location.date_effet_bail).toLocaleDateString()}</td>
                        <td>{location.date_fin_bail ? new Date(location.date_fin_bail).toLocaleDateString() : 'En cours'}</td>
                        <td>{formatPrice(location.prix_loyer)}</td>
                        <td>
                          {location.locataires.map(locataire =>
                            `${locataire.prenom} ${locataire.nom}`
                          ).join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  </table>
                </div>
              )}

            <input type="hidden" {...register('location_id')} />
          </div>
        )}

        <div className="col-span-3 flex justify-between">
          <button
            type="button"
            className="btn btn-outline"
            onClick={handleReset}
          >
            Annuler
          </button>
          <button
            type="submit"
            className={`btn ${isCreating ? 'loading' : ''} btn-primary text-white p-2`}
            disabled={isCreating}
          >
            {isCreating? <Spinner/> : 'Enregistrer'}
          </button>
        </div>
      </SectionPanel.Body>
    </SectionPanel>
    </form>
  )
}